// apps/hr/src/pages/hr/Employees.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, Plus, Edit, Trash2, 
  ChevronLeft, ChevronRight, X,
  Users, Briefcase, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 10;

  // Role options (excluding 'user' and 'admin' because those are not employees)
  const roleOptions = ['manager', 'sales', 'designer', 'hr', 'directors', 'construction_partners', 'interior_partners'];

  useEffect(() => {
    fetchEmployees();
  }, [searchTerm, filterRole, page]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      // Build filter: exclude user and admin roles
      let filters = [];
      filters.push('role != "user"');
      filters.push('role != "admin"');
      filters.push('role != "super_admin"');
      
      if (searchTerm) {
        filters.push(`(name ~ '${searchTerm}' || email ~ '${searchTerm}' || phone ~ '${searchTerm}')`);
      }
      if (filterRole) {
        filters.push(`role = '${filterRole}'`);
      }
      const filterQuery = filters.join(' && ');

      const result = await pb.collection('users').getList(page, perPage, {
        filter: filterQuery,
        sort: '-created',
        requestKey: null,
      });
      setEmployees(result.items);
      setTotalPages(result.totalPages);
    } catch (error) {
      if (error.isAbort) {
        console.log('Request auto-cancelled (this is normal).');
        return;
      }
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    try {
      await pb.collection('users').delete(id);
      toast.success('Employee deleted successfully');
      fetchEmployees();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete employee: ' + error.message);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterRole('');
    setPage(1);
  };

  const getRoleBadge = (role) => {
    const classes = {
      manager: 'bg-blue-100 text-blue-800',
      sales: 'bg-green-100 text-green-800',
      designer: 'bg-purple-100 text-purple-800',
      hr: 'bg-pink-100 text-pink-800',
      directors: 'bg-amber-100 text-amber-800',
      construction_partners: 'bg-orange-100 text-orange-800',
      interior_partners: 'bg-teal-100 text-teal-800',
    };
    return classes[role] || 'bg-slate-100 text-slate-800';
  };

  const formatRole = (role) => {
    if (!role) return 'N/A';
    return role.replace(/_/g, ' ').toUpperCase();
  };

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Employees</h1>
          <p className="text-sm text-slate-500">Manage all employees</p>
        </div>
        <Link 
          to="/employees/new" 
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition shadow-md text-sm"
        >
          <Plus className="w-4 h-4" /> Add Employee
        </Link>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
          >
            <option value="">All Roles</option>
            {roleOptions.map(role => (
              <option key={role} value={role}>{formatRole(role)}</option>
            ))}
          </select>
          {(searchTerm || filterRole) && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-sm text-amber-600 border border-amber-200 rounded-lg hover:bg-amber-50 transition flex items-center gap-1"
            >
              <X className="w-4 h-4" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Employee Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : employees.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-700">No employees found</h3>
          <p className="text-sm text-slate-500">Add your first employee</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Name</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Email</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Phone</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Role</th>
                  <th className="text-left px-4 py-3 text-slate-600 font-medium">Department</th>
                  <th className="text-center px-4 py-3 text-slate-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, idx) => (
                  <motion.tr 
                    key={emp.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-semibold">
                          {emp.name?.charAt(0) || 'U'}
                        </div>
                        <span className="font-medium text-slate-800">{emp.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{emp.email}</td>
                    <td className="px-4 py-3 text-slate-600">{emp.phone || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadge(emp.role)}`}>
                        {formatRole(emp.role)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{emp.department || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Link 
                          to={`/employees/edit/${emp.id}`}
                          className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(emp.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg disabled:opacity-50 hover:bg-slate-50"
              >
                <ChevronLeft className="w-4 h-4 inline" /> Previous
              </button>
              <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg disabled:opacity-50 hover:bg-slate-50"
              >
                Next <ChevronRight className="w-4 h-4 inline" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Employees;